/**
 * Brand Record → CSS custom properties. HANDOFF §3.
 *
 * RESPONSIBILITY: one pure function, `deriveBrandRecordTokens()`, turning a
 * `BrandRecord` + a paper + a depth into the exact property map to write.
 * No DOM. `BrandConfigService` is the only thing that writes it, and it does
 * nothing this file did not decide.
 *
 * Pure because contrast is a test, not a screenshot. Every claim §3 makes —
 * the action clears its paper, the label clears the action, the accent scale
 * is untouched — is an assertion against this function's output in
 * `brand-record-tokens.spec.ts`, on BOTH papers, for every fixture.
 *
 * AI CONTEXT: the interesting constraint is the NEGATIVE one. §3.3 says brand
 * must not touch status colours or the five categorical `--accent-*` pairs,
 * and "we tried rotating their hues per brand and it destroyed that". A
 * function that emits a property map can violate that silently, so the map is
 * filtered through `FORBIDDEN_PREFIXES` on the way out and the filter is
 * tested. Adding a per-tenant accent has to get past a failing test, which is
 * the point.
 */

import {
  contrastRatio,
  deriveActionTokens,
  hexToHsl,
  hslToHex,
  isValidHex,
  MIN_GRAPHIC_CONTRAST,
  MIN_TEXT_CONTRAST,
  PAPER_BASE,
  colorWithAlpha,
  type ActionTokens,
  type PaperKind,
} from './brand-color';
import type { BrandDepth, BrandRecord } from '../models/brand-record.model';

/**
 * §3.3 — brand is identity, not information.
 *
 * Green means free everywhere; a red brand cannot make "booked" red. The five
 * accent pairs are categorical — their job is mutual distinguishability for
 * avatars, marks and categories, and an 11° hue rotation made five swatches
 * indistinguishable. Nothing this module emits may begin with any of these.
 */
export const FORBIDDEN_PREFIXES = [
  '--accent-',
  '--success',
  '--warning',
  '--error',
  '--info',
  '--available',
  '--busy',
  '--occupied',
] as const;

/**
 * §1.7's prose says the paper takes 8–14% of the brand hue. The prototype's
 * own implementation then caps it at 4, with "4% ceiling keeps it a warm
 * near-neutral" — prose and code disagree and the code is the measured
 * answer. 4 is also the most a tint can take before it starts eating the
 * contrast headroom of everything sitting on it, which is the reason the
 * cap has to exist at all rather than being a taste call.
 */
export const MAX_TINT_PERCENT = 4;

export interface BrandTokenAudit {
  /** Resolved primary-action fill and its measured contrasts. */
  action: ActionTokens;
  /** The colour genuinely behind a control — paper, tinted if depth allows. */
  effectivePaper: string;
  /** Ink measured as a foreground on the effective paper. */
  inkContrastOnPaper: number;
  /** True when every floor in §3 is met on this paper. */
  passes: boolean;
  /** Human-readable reasons, empty when `passes`. */
  failures: string[];
}

export interface BrandTokenResult {
  props: Readonly<Record<string, string>>;
  audit: BrandTokenAudit;
}

/** Mix a brand hue into the paper without leaving sRGB or the hue behind. */
function tintPaper(paperHex: string, inkHex: string, percent: number): string {
  const p = Math.max(0, Math.min(MAX_TINT_PERCENT, percent)) / 100;
  if (p === 0) return paperHex;
  const a = hexToHsl(paperHex);
  const b = hexToHsl(inkHex);
  // Move hue and saturation toward the ink; leave lightness alone so the
  // paper stays the same paper. A tint that darkens is a different paper.
  return hslToHex(b.h, a.s + (b.s - a.s) * p, a.l);
}

/** Resolve `action.source` to a concrete hex before any paper adjustment. */
function requestedAction(record: BrandRecord, paper: PaperKind): string {
  const { source, value } = record.action;
  if (source === 'custom' && value && isValidHex(value)) return value;
  if (source === 'neutral') {
    // "The paper's own ink." Not a fixed hex — that is exactly the §1.5 trap
    // where a paper-invariant ink collapses onto a dark background at 1.0:1.
    // Near-black on light paper, near-white on dark, then handed to the same
    // derivation as anything else so it is measured rather than assumed.
    return paper === 'light' ? '#101418' : '#f2f2f2';
  }
  return record.ink;
}

/**
 * Turn a record into the properties to set on the token root.
 *
 * `depth: 'accent'` emits ONLY the colour axis — ink, the separated action,
 * and focus. That is today's behaviour plus §3.1, and it is what a cautious
 * customer gets. `depth: 'full'` adds paper tint, shape, type and the icon
 * stroke. Nothing in either path is conditional on a tenant identity.
 */
export function deriveBrandRecordTokens(
  record: BrandRecord,
  paper: PaperKind,
  depth: BrandDepth,
): BrandTokenResult {
  const full = depth === 'full';
  const ink = isValidHex(record.ink) ? record.ink : PAPER_BASE[paper];

  const effectivePaper = full
    ? tintPaper(PAPER_BASE[paper], ink, record.tint)
    : PAPER_BASE[paper];

  const action = deriveActionTokens(
    requestedAction(record, paper),
    paper,
    effectivePaper,
  );

  const props: Record<string, string> = {
    // ── §3.1 · the tokens that carry the rule ────────────────
    //
    // `--brand-ink` is DELIBERATELY ABSENT. §3 names it, and styles.scss
    // declares it — as an alias of `--brand-primary`, which resolves through
    // `--brand-color`, which `DivisionBrandingService` derives with the
    // 846-test `deriveBrandTokens`. Emitting a literal here would shadow all
    // of that with the raw record hex: easyJet's #FF6600 measures 2.70:1 on
    // cream, so anything lettering in the "brand ink" would drop below AA
    // the moment this service loaded. The ink axis stays owned by the
    // existing system; this one only adds what a single hex cannot carry.
    '--brand-action': action.action,
    '--brand-on-action': action.onAction,
    '--brand-action-hover': action.hover,
  };

  if (full) {
    // ── Paper ──────────────────────────────────────────────
    props['--brand-tint'] = effectivePaper;
    props['--brand-wash'] = colorWithAlpha(ink, 0.07);

    // ── Shape ──────────────────────────────────────────────
    const s = record.shape;
    props['--radius'] = `${s.container}px`;
    props['--radius-lg'] = `${Math.round(s.container * 0.875)}px`;
    props['--radius-md'] = `${s.control}px`;
    props['--radius-sm'] = `${Math.max(2, Math.round(s.control * 0.66))}px`;
    props['--radius-pill'] = `${s.badge}px`;
    props['--brand-border-width'] = `${s.border}px`;
    props['--brand-avatar-shape'] = s.avatar;
    // §3.2 lists `iconStroke` as a brand field, and the difference between
    // 1.5 and 2.5 is most of what separates "financial" from "consumer" at a
    // glance. It is a token here and nothing reads it yet — see the report.
    props['--icon-stroke'] = String(s.iconStroke);

    // ── Type ───────────────────────────────────────────────
    const t = record.type;
    props['--font-display'] = t.display;
    props['--font-ui'] = t.ui;
    // §3.4 — the weight map. Without it a face with no 600 snaps to 400 and
    // the hierarchy flattens with no error anywhere.
    props['--fw-medium'] = String(t.weights.medium);
    props['--fw-semibold'] = String(t.weights.semibold);
    props['--fw-bold'] = String(t.weights.bold);
    props['--brand-case-display'] = t.caseDisplay;
    props['--brand-track-display'] = t.trackDisplay;

    // ── Finesse ────────────────────────────────────────────
    props['--brand-marker'] = `"${record.markerGlyph}"`;
  }

  // §3.3 is enforced here, not by convention. A property that would recolour
  // a categorical or semantic scale never leaves this function.
  for (const key of Object.keys(props)) {
    if (FORBIDDEN_PREFIXES.some(p => key.startsWith(p))) delete props[key];
  }

  // ── Audit ────────────────────────────────────────────────
  const inkContrastOnPaper = contrastRatio(ink, effectivePaper);
  const failures: string[] = [];
  if (action.contrastOnPaper < MIN_GRAPHIC_CONTRAST) {
    failures.push(
      `action fill ${action.action} is ${action.contrastOnPaper.toFixed(2)}:1 on ${effectivePaper}`,
    );
  }
  if (action.contrastOnAction < MIN_TEXT_CONTRAST) {
    failures.push(
      `action label ${action.onAction} is ${action.contrastOnAction.toFixed(2)}:1 on ${action.action}`,
    );
  }

  return {
    props,
    audit: {
      action,
      effectivePaper,
      inkContrastOnPaper,
      passes: failures.length === 0,
      failures,
    },
  };
}
